/**
 * Какая ссылка породит карточку превью. Порт TDLib `get_first_url`.
 * 05-ui.md §3.4, docs/telegram.md ПРАВИЛА 5.1, 5.2, 5.2а, 5.4, 8.5.
 *
 * ТОЛЬКО для UI: наружу — через вход `@smm/contracts/preview`, ровно как `detect/`
 * (§Хребет 3.5, §Хребет 6). В основном барелле `.` этого модуля нет и быть не должно:
 * бэкенд карточку не считает (09-blockers-closed.md §9.1), а сверка после публикации
 * её и не может проверить — поля `web_page` в ответе Bot API нет (ПРАВИЛО 6.2).
 *
 * ── По каким сущностям идём: по expectedServerEntities, а не по canonical.entities ──
 *
 * Вопрос неочевидный, поэтому решение записано здесь целиком.
 *
 * `MessageEntitySchema` (draft.ts) не содержит варианта `type: 'url'` вовсе — отправить
 * такую сущность явно нельзя, её ставит сервер (`AUTO_DETECTED_ENTITY_TYPES`). Значит,
 * пойди мы строго по `canonical.entities`, ветка «сущность url» была бы мёртвым кодом, а
 * самый частый случай — голая ссылка в тексте — давал бы в интерфейсе «карточки не будет»
 * при том, что в канале карточка появится. Это ровно та ложь, которую запрещает ПРАВИЛО
 * 5.4: «в UI видно, будет ли карточка превью и какая ссылка её порождает».
 *
 * Документация Bot API про `LinkPreviewOptions.url` говорит «the first URL **found in the
 * message text**» — ищет сервер, своим детектором, уже после автоопределения сущностей
 * (ПРАВИЛО 3.1). Поэтому вход функции — то же самое `expected`, по которому рисуется само
 * превью: `expectedServerEntities(canonical)` = `canonical.entities` ∪ `detectEntities(text)`,
 * с вычетом того, что сервер внутри `code`/`pre`/`text_link` и на границе цитаты не ставит.
 *
 * Противоречия с §Хребет 3.5 тут нет: запрет на детектор действует для канонизации, хэша и
 * сверки — там вердикт нельзя связывать с версией нашего детектора. Превью, наоборот,
 * ОБЯЗАНО автоопределение воспроизводить (ПРАВИЛО 3.2), иначе оно чище публикации.
 */

import { expectedServerEntities } from './detect/index.ts';
import {
  type DocumentDescriptor,
  type LinkPreviewOptions,
  type MediaDescriptor,
  type MessageEntity,
  wireMethodOf,
} from './draft.ts';

/** То, что видит человек: будет карточка или нет — и в обоих случаях почему. */
export type FirstUrlOutcome =
  | {
      kind: 'none';
      reason:
        | 'disabled'
        | 'rich_media'
        | 'rich_document'
        | 'rich_length'
        | 'rich_policy'
        | 'no_url';
    }
  | { kind: 'card'; url: string; host: string; source: 'explicit' | 'url_entity' | 'text_link' };

/**
 * Подмножество `DraftContent`, от которого зависит карточка. `CanonicalDraft` подходит.
 *
 * СПОСОБ ОТПРАВКИ — ДВА ВХОДА, И ПУТАТЬ ИХ НЕЛЬЗЯ (19.09.2026).
 *   • `method` — только у ЗАМОРОЖЕННОГО содержания (сохранённая версия, экран одобрения): он
 *     подписан, и публикатор отправит ровно его. С политикой бренда «все посты — расширенным
 *     сообщением» короткий пост без картинки законно rich, и вывести это по длине нельзя.
 *   • `richAlways` — политика бренда СЕЙЧАС, для ещё не сохранённой правки в редакторе. Её
 *     `method` передавать нельзя: он остался от прошлой версии, и превью показало бы карточку
 *     по вчерашнему решению (ровно поэтому `method` отсюда однажды и убирали). Способ отправки
 *     тогда ВЫВОДИТСЯ тем же правилом, которым его выберет заморозка при сохранении.
 */
export interface FirstUrlInput {
  text: string;
  entities: readonly MessageEntity[];
  link_preview_options: LinkPreviewOptions | null;
  media: MediaDescriptor | null;
  /** PDF внизу поста (§9.2): его наличие делает пост rich. Нет поля — документа нет. */
  document?: DocumentDescriptor | null | undefined;
  /** Способ отправки ЗАМОРОЖЕННОГО содержания — см. шапку; есть — решает он. */
  method?: 'sendMessage' | 'sendRichMessage' | undefined;
  /** Политика бренда для незамороженной правки — см. шапку; действует, только если `method` нет. */
  richAlways?: boolean | undefined;
}

/**
 * Схемы, для которых Telegram карточку не строит. Проверяем префиксом, как TDLib
 * (`begins_with(url, "ton:")`), а не разбором схемы: `example.com:8080/x` формально
 * подходит под грамматику схемы и разбором был бы испорчен.
 */
const SKIPPED_SCHEMES = ['ton:', 'tg:', 'ftp:', 'tonsite:'] as const;

const hasSkippedScheme = (url: string): boolean => {
  const head = url.slice(0, 9).toLowerCase();
  return SKIPPED_SCHEMES.some((s) => head.startsWith(s));
};

const SCHEME_SLASHES = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//;

/**
 * Разбор без `new URL()`: конструктор бросает на `geoboost.ru/blog` (нет схемы) — а это
 * ровно та форма, которую возвращает детектор. Исключений наружу не выпускаем вовсе:
 * функция вызывается на каждом рендере превью, и падать ей нельзя.
 *
 * `rest` — всё после authority. Пустой `rest` и означает «голый домен»: `geoboost.ru` и
 * `https://geoboost.ru` трактуются одинаково, потому что `//` после схемы путём не является.
 */
function splitUrl(url: string): { host: string; rest: string } {
  const afterScheme = url.replace(SCHEME_SLASHES, '');
  let end = afterScheme.length;
  for (const c of ['/', '?', '#']) {
    const i = afterScheme.indexOf(c);
    if (i >= 0 && i < end) end = i;
  }
  let authority = afterScheme.slice(0, end);
  const at = authority.lastIndexOf('@');
  if (at >= 0) authority = authority.slice(at + 1);
  const colon = authority.lastIndexOf(':');
  if (colon >= 0 && /^\d*$/.test(authority.slice(colon + 1))) authority = authority.slice(0, colon);
  return { host: authority.toLowerCase(), rest: afterScheme.slice(end) };
}

/** Хост для плашки «Telegram нарисует карточку из <host>» (05-ui.md §3.4 п.4). */
export function hostOfUrl(url: string): string {
  const { host } = splitUrl(url);
  // Разобрать не удалось — показываем ссылку целиком, а не пустую строку: человеку нужно
  // понять, о какой ссылке речь, и «из <ничего>» этого не даёт.
  return host === '' ? url : host;
}

const card = (url: string, source: 'explicit' | 'url_entity' | 'text_link'): FirstUrlOutcome => ({
  kind: 'card',
  url,
  host: hostOfUrl(url),
  source,
});

/**
 * Порядок проверок нормативен (05-ui.md §3.4 п.1–2) и на достижимых входах однозначен:
 * у rich-поста схема требует `link_preview_options === null`, так что ветки `disabled` и
 * `rich_*` не конкурируют.
 *
 * Явный `link_preview_options.url` побеждает безусловно — это осознанный выбор человека
 * («Взять другую ссылку», 05-ui.md §3.4), и список пропускаемых схем к нему не применяется:
 * он моделирует детектор сервера, а не второе мнение о заданном руками поле.
 */
export function getFirstUrl(content: FirstUrlInput): FirstUrlOutcome {
  const lpo = content.link_preview_options;
  if (lpo?.is_disabled === true) return { kind: 'none', reason: 'disabled' };
  // Карточки не бывает у rich-сообщения: параметра `link_preview_options` у метода нет
  // вовсе. Причин уехать rich четыре, и человеку они говорят РАЗНОЕ — «картинка заняла это
  // место», «к посту приложен файл», «пост слишком длинный» и «так решено в Настройках», —
  // поэтому и reason'ов четыре. Причина берётся в порядке выбора метода при заморозке.
  const document = content.document ?? null;
  const rich =
    content.method !== undefined
      ? content.method === 'sendRichMessage'
      : wireMethodOf(
          { text: content.text, media: content.media, document },
          { richAlways: content.richAlways === true },
        ) === 'sendRichMessage';
  if (rich) {
    if (content.media !== null) return { kind: 'none', reason: 'rich_media' };
    if (document !== null) return { kind: 'none', reason: 'rich_document' };
    return {
      kind: 'none',
      reason:
        wireMethodOf({ text: content.text, media: null }) === 'sendRichMessage'
          ? 'rich_length'
          : 'rich_policy',
    };
  }
  if (lpo !== null && lpo.url !== null && lpo.url !== '') return card(lpo.url, 'explicit');

  for (const e of expectedServerEntities(content)) {
    if (e.type === 'url') {
      // Длина в UTF-16, как везде в сущностях (ПРАВИЛО 2.1).
      if (e.length <= 4) continue;
      const raw = content.text.slice(e.offset, e.offset + e.length);
      if (hasSkippedScheme(raw)) continue;
      // Контринтуитивное правило §5.2а: голый домен карточку НЕ даёт (geoboost.ru),
      // а с путём — даёт (geoboost.ru/blog).
      if (splitUrl(raw).rest === '') continue;
      return card(raw, 'url_entity');
    }
    if (e.type === 'text_link') {
      // Второе контринтуитивное правило §5.2а: ссылка, спрятанная под анкором, карточку
      // всё равно притащит. Ограничения «голого домена» здесь нет — TDLib берёт адрес
      // сущности как есть.
      const url = e.url;
      if (url === undefined || url === '' || hasSkippedScheme(url)) continue;
      return card(url, 'text_link');
    }
    // Остальные автоопределяемые типы (mention, hashtag, email) карточку не дают.
  }

  return { kind: 'none', reason: 'no_url' };
}
